import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, FileText, Image as ImageIcon, Loader2, Save, Sparkles } from "lucide-react";
import DynamicList from "../../components/dynamic/List";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import checkPermission from "../../utils/permission";
import type {
  MaarifDifficulty,
  MaarifGeneratedPreview,
  MaarifGeneratedQuestion,
  MaarifSourceMode,
} from "../../types/maarif-test-generator";

const gradeOptions = Array.from({ length: 12 }, (_, index) => `${index + 1}. Sınıf`);
const difficultyOptions: MaarifDifficulty[] = ["Kolay", "Orta", "Zor"];
const choiceKeys: Array<"A" | "B" | "C" | "D" | "E"> = ["A", "B", "C", "D", "E"];
const choiceCountOptions = [2, 3, 4, 5];
const MAX_SELECTED_CONTENTS = 3;
const SELECTABLE_CONTENT_TYPES = new Set(["FILE", "EXAM"]);
const sourceModeLabels: Record<MaarifSourceMode, string> = {
  topic: "Konu odaklı",
  tests: "Test mantığı referanslı",
  files: "Dosya odaklı",
  mixed: "Dosya + test referanslı",
};

type GeneratorLesson = {
  id: string;
  title: string;
};

type GeneratorSectionContent = {
  id: string;
  title: string;
  type: "FILE" | "EXAM";
  content?: string | null;
  sectionId: string;
  sectionTitle: string;
};

type GeneratorSection = {
  id: string;
  title: string;
  contents: GeneratorSectionContent[];
};

const createEmptyPreview = (): MaarifGeneratedPreview | null => null;
const getVisibleChoiceKeys = (choiceCount?: number) =>
  choiceKeys.slice(0, Math.max(2, Math.min(choiceKeys.length, Number(choiceCount) || choiceKeys.length)));

export default function MaarifTestGeneratorWorkspacePage() {
  const navigate = useNavigate();
  const { showAlert, showToast } = useModal();
  const canCreate = checkPermission("maarif_test_add");
  const [lessons, setLessons] = useState<GeneratorLesson[]>([]);
  const [sections, setSections] = useState<GeneratorSection[]>([]);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [loadingSections, setLoadingSections] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentUnitFilter, setContentUnitFilter] = useState<string>("all");
  const [preview, setPreview] = useState<MaarifGeneratedPreview | null>(createEmptyPreview);
  const [form, setForm] = useState({
    testAdi: "",
    lessonId: "",
    sectionIds: [] as string[],
    selectedContentIds: [] as string[],
    gradeLabel: "7. Sınıf",
    topic: "",
    difficulty: "Orta" as MaarifDifficulty,
    questionCount: 10,
    choiceCount: 5,
    imageQuestionCount: 0,
    pdf: null as File | null,
  });

  useEffect(() => {
    const loadLessons = async () => {
      try {
        const response = await ApiService.getAllLessons();
        const lessonRows = Array.isArray(response) ? response : response?.data || [];
        const mappedLessons = lessonRows.map((lesson: any) => ({
          id: lesson.id,
          title: lesson.title,
        }));
        setLessons(mappedLessons);
        if (mappedLessons[0]?.id) {
          setForm((prev) => ({
            ...prev,
            lessonId: prev.lessonId || mappedLessons[0].id,
          }));
        }
      } catch (error: any) {
        await showAlert({
          title: "Dersler yüklenemedi",
          message: error?.message || "Ders listesi alınamadı.",
          type: "error",
        });
      } finally {
        setLoadingLessons(false);
      }
    };

    void loadLessons();
  }, [showAlert]);

  useEffect(() => {
    if (!form.lessonId) {
      setSections([]);
      return;
    }

    let isCancelled = false;
    const loadLessonSections = async () => {
      setLoadingSections(true);
      try {
        const response = await ApiService.getLessonPreview(form.lessonId);
        if (isCancelled) return;

        const sectionRows = Array.isArray(response?.sections) ? response.sections : [];
        const mappedSections: GeneratorSection[] = sectionRows.map((section: any) => ({
          id: section.id,
          title: section.title || "İsimsiz Bölüm",
          contents: (Array.isArray(section.contents) ? section.contents : [])
            .filter((content: any) => SELECTABLE_CONTENT_TYPES.has(String(content?.type || "").toUpperCase()))
            .map((content: any) => ({
              id: content.id,
              title: content.title || "İsimsiz İçerik",
              type: String(content.type || "").toUpperCase() === "EXAM" ? "EXAM" : "FILE",
              content: content.content || "",
              sectionId: section.id,
              sectionTitle: section.title || "İsimsiz Bölüm",
            })),
        }));

        setSections(mappedSections);
        setForm((prev) => {
          const allowedSectionIds = new Set(mappedSections.map((section) => section.id));
          const nextSectionIds = prev.sectionIds.filter((sectionId) => allowedSectionIds.has(sectionId));
          const allowedContentIds = new Set(
            mappedSections
              .filter((section) => nextSectionIds.includes(section.id))
              .flatMap((section) => section.contents.map((content) => content.id))
          );
          const nextSelectedContentIds = prev.selectedContentIds.filter((contentId) => allowedContentIds.has(contentId));

          if (
            nextSectionIds.length === prev.sectionIds.length &&
            nextSelectedContentIds.length === prev.selectedContentIds.length
          ) {
            return prev;
          }

          return {
            ...prev,
            sectionIds: nextSectionIds,
            selectedContentIds: nextSelectedContentIds,
          };
        });
      } catch (error: any) {
        if (isCancelled) return;
        setSections([]);
        await showAlert({
          title: "Bölümler yüklenemedi",
          message: error?.message || "Seçilen dersin bölümleri alınamadı.",
          type: "error",
        });
      } finally {
        if (!isCancelled) {
          setLoadingSections(false);
        }
      }
    };

    void loadLessonSections();

    return () => {
      isCancelled = true;
    };
  }, [form.lessonId, showAlert]);

  const selectedLessonTitle = useMemo(
    () => lessons.find((lesson) => lesson.id === form.lessonId)?.title || "",
    [lessons, form.lessonId]
  );

  const selectedSections = useMemo(
    () => sections.filter((section) => form.sectionIds.includes(section.id)),
    [sections, form.sectionIds]
  );

  const selectableContents = useMemo(
    () => selectedSections.flatMap((section) => section.contents),
    [selectedSections]
  );

  const selectedSectionTitlesLabel = useMemo(
    () => selectedSections.map((section) => section.title).join(" / "),
    [selectedSections]
  );

  useEffect(() => {
    if (contentUnitFilter === "all") return;
    if (!selectedSections.some((section) => section.id === contentUnitFilter)) {
      setContentUnitFilter("all");
    }
  }, [contentUnitFilter, selectedSections]);

  const handleToggleSelectedContent = (contentId: string) => {
    let limitReached = false;

    setForm((prev) => {
      const alreadySelected = prev.selectedContentIds.includes(contentId);
      if (alreadySelected) {
        return {
          ...prev,
          selectedContentIds: prev.selectedContentIds.filter((id) => id !== contentId),
        };
      }

      if (prev.selectedContentIds.length >= MAX_SELECTED_CONTENTS) {
        limitReached = true;
        return prev;
      }

      return {
        ...prev,
        selectedContentIds: [...prev.selectedContentIds, contentId],
      };
    });

    if (limitReached) {
      showToast(`En fazla ${MAX_SELECTED_CONTENTS} içerik seçebilirsiniz.`, "warning");
    }
  };

  const handleToggleSection = (sectionId: string) => {
    setForm((prev) => {
      const nextSectionIds = prev.sectionIds.includes(sectionId)
        ? prev.sectionIds.filter((id) => id !== sectionId)
        : [...prev.sectionIds, sectionId];
      const allowedContentIds = new Set(
        sections
          .filter((section) => nextSectionIds.includes(section.id))
          .flatMap((section) => section.contents.map((content) => content.id))
      );
      const nextSelectedContentIds = prev.selectedContentIds.filter((contentId) => allowedContentIds.has(contentId));
      const nextTopic = sections
        .filter((section) => nextSectionIds.includes(section.id))
        .map((section) => section.title)
        .join(" / ");

      return {
        ...prev,
        sectionIds: nextSectionIds,
        selectedContentIds: nextSelectedContentIds,
        topic: nextTopic,
      };
    });
  };

  const sectionContentListFetcher = async (_page: number, searchTerm?: string) => {
    const normalizedSearch = (searchTerm || "").trim().toLocaleLowerCase("tr");
    const unitFilteredContents =
      contentUnitFilter === "all" ? selectableContents : selectableContents.filter((content) => content.sectionId === contentUnitFilter);
    const filteredData = normalizedSearch
      ? unitFilteredContents.filter((content) => {
          const haystack = `${content.title} ${content.sectionTitle} ${content.type}`.toLocaleLowerCase("tr");
          return haystack.includes(normalizedSearch);
        })
      : unitFilteredContents;

    return {
      data: filteredData,
      meta: {
        page: 1,
        limit: filteredData.length || 5,
        totalItems: filteredData.length,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      },
    };
  };

  const handleGenerate = async () => {
    if (!form.testAdi.trim()) {
      await showAlert({ title: "Uyarı", message: "Test adı zorunludur.", type: "warning" });
      return;
    }

    if (!form.lessonId) {
      await showAlert({ title: "Uyarı", message: "Ders seçmelisiniz.", type: "warning" });
      return;
    }

    if (!form.topic.trim() && !form.pdf && form.selectedContentIds.length === 0) {
      await showAlert({
        title: "Uyarı",
        message: "Konu yazmalı, bölüm/içerik seçmeli veya PDF yüklemelisiniz.",
        type: "warning",
      });
      return;
    }

    setPreview(createEmptyPreview());
    setGenerating(true);
    try {
      const result = await ApiService.generateMaarifTest({
        testAdi: form.testAdi.trim(),
        lessonId: form.lessonId,
        sectionIds: form.sectionIds,
        selectedContentIds: form.selectedContentIds,
        gradeLabel: form.gradeLabel,
        topic: form.topic.trim(),
        difficulty: form.difficulty,
        questionCount: form.questionCount,
        choiceCount: form.choiceCount,
        imageQuestionCount: Math.min(form.imageQuestionCount, form.questionCount),
        pdf: form.pdf,
      });
      setPreview(result);
      showToast("Test oluştu. Aşağıdan kontrol edebilirsiniz.", "success");
    } catch (error: any) {
      await showAlert({
        title: "Üretim hatası",
        message: error?.message || "Test oluşturulamadı.",
        type: "error",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!preview) return;

    setSaving(true);
    try {
      await ApiService.saveMaarifTest({
        testAdi: form.testAdi.trim(),
        preview: {
          ...preview,
          testAdi: form.testAdi.trim(),
          lessonTitle: selectedLessonTitle || preview.lessonTitle,
        },
      });
      showToast("Test Dosyalar'a kaydedildi ve Dizgici için hazırlandı.", "success");
      navigate("/files");
    } catch (error: any) {
      await showAlert({
        title: "Kaydetme hatası",
        message: error?.message || "Test kaydedilemedi.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (index: number, updater: (question: MaarifGeneratedQuestion) => MaarifGeneratedQuestion) => {
    setPreview((prev) => {
      if (!prev) return prev;
      const questions = prev.questions.map((question, questionIndex) =>
        questionIndex === index ? updater(question) : question
      );
      return {
        ...prev,
        questions,
      };
    });
  };

  const previewChoiceKeys = getVisibleChoiceKeys(preview?.choiceCount || form.choiceCount);

  if (!canCreate) {
    return (
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="text-lg font-semibold text-gray-900">Test Oluşturma erişimi yok</div>
          <div className="mt-2 text-sm text-gray-600">Bu modülü kullanma yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  }

  const workspaceContent = (
    <div className="h-full bg-gray-50 overflow-y-auto">
      <div className="min-h-full bg-gray-50 px-6 py-6">
        <div className="mx-auto max-w-6xl">
          <div className="overflow-visible">
            <div className="grid grid-cols-1 gap-6">
              <div className="min-h-0 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm xl:mx-auto xl:w-full xl:max-w-4xl">
                <div className="space-y-5">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Test Adı</label>
                    <input
                      value={form.testAdi}
                      onChange={(event) => setForm((prev) => ({ ...prev, testAdi: event.target.value }))}
                      placeholder="Örn. 7. Sınıf Kesir Problemleri Testi"
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Ders</label>
                    <select
                      value={form.lessonId}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          lessonId: event.target.value,
                          sectionIds: [],
                          selectedContentIds: [],
                          topic: "",
                        }))
                      }
                      disabled={loadingLessons}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400 disabled:bg-slate-100"
                    >
                      {lessons.map((lesson) => (
                        <option key={lesson.id} value={lesson.id}>
                          {lesson.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-700">Bölümler</label>
                      <span className="text-xs text-slate-500">{form.sectionIds.length} bölüm seçildi</span>
                    </div>
                    {!form.lessonId ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                        Önce ders seçin.
                      </div>
                    ) : loadingSections ? (
                      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-500">
                        Bölümler yükleniyor...
                      </div>
                    ) : sections.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                        Bu derse ait bölüm bulunamadı.
                      </div>
                    ) : (
                      <div className="flex max-h-52 flex-wrap gap-2 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-3">
                        {sections.map((section) => {
                          const isSelected = form.sectionIds.includes(section.id);
                          return (
                            <button
                              key={section.id}
                              type="button"
                              onClick={() => handleToggleSection(section.id)}
                              className={`rounded-full border px-3 py-2 text-sm font-medium transition ${
                                isSelected
                                  ? "border-blue-500 bg-blue-50 text-blue-700"
                                  : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50"
                              }`}
                            >
                              {section.title}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      Birden fazla bölüm seçebilirsiniz. Konu alanı seçilen bölüm adlarıyla otomatik doldurulur.
                      {selectedSectionTitlesLabel ? ` Aktif seçim: ${selectedSectionTitlesLabel}` : ""}
                    </p>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-sm font-medium text-slate-700">Bölüm İçerikleri</label>
                      <span className="text-xs text-slate-500">{form.selectedContentIds.length} / {MAX_SELECTED_CONTENTS} seçildi</span>
                    </div>

                    {form.sectionIds.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                        Önce en az bir bölüm seçin. Bu alanda sadece dosya ve test içerikleri gösterilir, video içerikleri gizlenir.
                      </div>
                    ) : selectableContents.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm text-slate-500">
                        Seçilen bölümlerde seçilebilir dosya veya test içeriği bulunmuyor.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Ünite Filtresi</div>
                              <p className="mt-1 text-xs text-slate-500">Listeyi seçili üniteler içinde daraltabilirsiniz.</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => setContentUnitFilter("all")}
                                className={`rounded-full border px-3 py-2 text-xs font-semibold transition ${
                                  contentUnitFilter === "all"
                                    ? "border-blue-600 bg-blue-600 text-white"
                                    : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50"
                                }`}
                              >
                                Tüm Üniteler
                              </button>
                              {selectedSections.map((section) => (
                                <button
                                  key={section.id}
                                  type="button"
                                  onClick={() => setContentUnitFilter(section.id)}
                                  className={`rounded-full border px-3 py-2 text-xs font-semibold transition ${
                                    contentUnitFilter === section.id
                                      ? "border-blue-600 bg-blue-50 text-blue-700"
                                      : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-slate-50"
                                  }`}
                                >
                                  {section.title}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white">
                        <DynamicList
                          header={false}
                          title="Bölüm İçerikleri"
                          path="maarif-selected-contents"
                          fetcher={sectionContentListFetcher}
                          item={(content: GeneratorSectionContent) => {
                            const isSelected = form.selectedContentIds.includes(content.id);
                            const isFile = content.type === "FILE";
                            return (
                              <button
                                key={content.id}
                                type="button"
                                onClick={() => handleToggleSelectedContent(content.id)}
                                className={`mb-3 w-full rounded-2xl border px-4 py-3 text-left transition ${
                                  isSelected
                                    ? "border-blue-500 bg-blue-50 shadow-sm"
                                    : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div
                                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                      isFile ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"
                                    }`}
                                  >
                                    {isFile ? <FileText className="h-5 w-5" /> : <ClipboardCheck className="h-5 w-5" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <div className="truncate text-sm font-semibold text-slate-900">{content.title}</div>
                                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                                        {content.sectionTitle}
                                      </span>
                                      <span
                                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                          isFile ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
                                        }`}
                                      >
                                        {isFile ? "Dosya" : "Test"}
                                      </span>
                                    </div>
                                    <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">
                                      {isFile
                                        ? "Bu dosya seçilirse yaoay zeka konuya göre soruları sıfırdan yazar."
                                        : "Bu test seçilirse yapay zeka soruların mantığını Maarif modeline uygun biçimde yeniden kurar."}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          }}
                          isSearch
                          isPagination
                          clientPageSize={5}
                          refreshKey={`${form.lessonId}-${form.sectionIds.join(",")}-${form.selectedContentIds.join(",")}-${contentUnitFilter}`}
                          emptyMessage="Aramaya uygun içerik bulunamadı."
                        />
                      </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Sınıf</label>
                    <select
                      value={form.gradeLabel}
                      onChange={(event) => setForm((prev) => ({ ...prev, gradeLabel: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                    >
                      {gradeOptions.map((grade) => (
                        <option key={grade} value={grade}>
                          {grade}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Konu</label>
                    <textarea
                      value={form.topic}
                      onChange={(event) => setForm((prev) => ({ ...prev, topic: event.target.value }))}
                      rows={4}
                      placeholder="Örn. Kesirlerde sıralama ve günlük yaşam problemleri"
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Bölüm seçerseniz alan otomatik doldurulur. İsterseniz düzenleyebilirsiniz. Dosya seçilirse sorular bu konuya göre
                      baştan yazılır.
                    </p>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Harici PDF İçeriği</label>
                    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-600 hover:border-blue-300">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="truncate">{form.pdf ? form.pdf.name : "PDF seçin"}</span>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] || null;
                          setForm((prev) => ({ ...prev, pdf: file }));
                        }}
                      />
                    </label>
                    <p className="mt-1 text-xs text-slate-500">
                      İsterseniz seçilen ders içeriklerine ek olarak ayrı bir PDF de yükleyebilirsiniz.
                    </p>
                  </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Soru Sayısı</label>
                  <input
                        type="number"
                        min={1}
                        max={40}
                        value={form.questionCount}
                        onChange={(event) => {
                          const questionCount = Math.max(1, Math.min(40, Number(event.target.value) || 1));
                          setForm((prev) => ({
                            ...prev,
                            questionCount,
                            imageQuestionCount: Math.min(prev.imageQuestionCount, questionCount),
                          }));
                        }}
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Şık Sayısı</label>
                  <select
                    value={form.choiceCount}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        choiceCount: Math.max(2, Math.min(5, Number(event.target.value) || 5)),
                      }))
                    }
                    className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                  >
                    {choiceCountOptions.map((count) => (
                      <option key={count} value={count}>
                        {count} şık
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Görselli Soru</label>
                  <input
                        type="number"
                        min={0}
                        max={form.questionCount}
                        value={form.imageQuestionCount}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            imageQuestionCount: Math.min(prev.questionCount, Math.max(0, Number(event.target.value) || 0)),
                          }))
                        }
                        className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Zorluk</label>
                    <div className="grid grid-cols-3 gap-2">
                      {difficultyOptions.map((difficulty) => (
                        <button
                          key={difficulty}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, difficulty }))}
                          className={`rounded-xl border px-3 py-3 text-sm font-medium transition ${
                            form.difficulty === difficulty
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {difficulty}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={generating || loadingLessons || loadingSections}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {generating ? "Test oluşturuluyor..." : "Testi Oluştur"}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate("/maarif-test-generator")}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Listeye Dön
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={!preview || saving}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {saving ? "Kaydediliyor..." : "Dosyalar'a Kaydet"}
                    </button>
                  </div>
                </div>
              </div>

              {(generating || preview) && (
                <div className="min-h-0 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm xl:mx-auto xl:w-full xl:max-w-4xl">
                  {generating && !preview ? (
                    <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-blue-200 bg-blue-50/60 px-6 text-center">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-blue-600 shadow-sm">
                        <Loader2 className="h-7 w-7 animate-spin" />
                      </div>
                      <h2 className="mt-4 text-lg font-semibold text-slate-900">Önizleme hazırlanıyor</h2>
                      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                        Yapay zeka seçtiğiniz ders, bölüm, içerik ve konuya göre TYMM uyumlu soruları hazırlıyor. İşlem tamamlanınca
                        sonuçlar burada otomatik olarak açılacak.
                      </p>
                    </div>
                  ) : preview ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 lg:col-span-2">
                        <div className="text-sm font-semibold text-slate-900">Bağlam Özeti</div>
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600">{preview.context || "Bağlam özeti yok."}</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="text-sm font-semibold text-emerald-900">TYMM Denetim Özeti</div>
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-emerald-800">
                          {preview.auditSummary || "Denetim özeti yok."}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{preview.lessonTitle}</span>
                      {Array.isArray(preview.sectionTitles) && preview.sectionTitles.length > 0 ? (
                        <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                          {preview.sectionTitles.join(" / ")}
                        </span>
                      ) : null}
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{preview.gradeLabel}</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{preview.difficulty}</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{preview.questionCount} soru</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">{preview.choiceCount} şık</span>
                    <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                      {preview.imageQuestionCount} görselli soru
                    </span>
                      {preview.sourceMode ? (
                        <span className="rounded-full bg-blue-50 px-3 py-1 font-medium text-blue-700">
                          {sourceModeLabels[preview.sourceMode] || preview.sourceMode}
                        </span>
                      ) : null}
                    </div>

                    <div className="space-y-4">
                      {preview.questions.map((question, index) => (
                        <div key={question.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-base font-semibold text-slate-900">Soru {index + 1}</div>
                              <div className="mt-1 text-xs text-slate-500">
                                Doğru cevap: {question["doğru_cevap"] || "Seçilmedi"} • Düzen:{" "}
                                {question["görsel"] ? "Görselli" : "Metin ağırlıklı"}
                              </div>
                            </div>
                            {question["görsel"] ? (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                                <ImageIcon className="h-3.5 w-3.5" />
                                Görselli
                              </span>
                            ) : null}
                          </div>

                          {question["görsel"] ? (
                            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                              <img
                                src={`data:${question["görsel"].mimeType};base64,${question["görsel"].base64}`}
                                alt={`Soru ${index + 1} görseli`}
                                className="mx-auto max-h-80 w-auto rounded-xl object-contain"
                              />
                            </div>
                          ) : null}

                          <div className="mt-4">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Soru Metni</label>
                            <textarea
                              value={question.soru_metni}
                              onChange={(event) =>
                                updateQuestion(index, (current) => ({
                                  ...current,
                                  soru_metni: event.target.value,
                                }))
                              }
                              rows={6}
                              className="w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                            />
                          </div>

                          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                            {previewChoiceKeys.map((choiceKey) => (
                              <div key={choiceKey}>
                                <label className="mb-2 block text-sm font-medium text-slate-700">{choiceKey} Şıkkı</label>
                                <input
                                  value={question["şıklar"][choiceKey]}
                                  onChange={(event) =>
                                    updateQuestion(index, (current) => ({
                                      ...current,
                                      "şıklar": {
                                        ...current["şıklar"],
                                        [choiceKey]: event.target.value,
                                      },
                                    }))
                                  }
                                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                                />
                              </div>
                            ))}
                          </div>

                          <div className="mt-4 max-w-xs">
                            <label className="mb-2 block text-sm font-medium text-slate-700">Doğru Cevap</label>
                            <select
                              value={question["doğru_cevap"] || ""}
                              onChange={(event) =>
                                updateQuestion(index, (current) => ({
                                  ...current,
                                  "doğru_cevap": (event.target.value || null) as MaarifGeneratedQuestion["doğru_cevap"],
                                }))
                              }
                              className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400"
                            >
                              <option value="">Seçin</option>
                              {previewChoiceKeys.map((choiceKey) => (
                                <option key={choiceKey} value={choiceKey}>
                                  {choiceKey}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs: TabItemProps[] = [
    {
      type: "custom",
      title: "Üretim Paneli",
      path: "generator-workspace",
      tabId: "generator-workspace",
      customComponent: workspaceContent,
    },
  ];

  return <TabPage title="Test Oluşturma" tabs={tabs} />;
}
